import { Formik } from 'formik'
import React from 'react'
import { Input, Dropdown, Checkboxes } from 'src/components/Forms'
import { Body, Footer } from 'src/components/theme/Models/Modal'
import { faSave, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons'
import { lookupOptionsDocument, useLookupConfig, useLookupOptions, useMutateUpdateLookupOption } from 'src/queries'
import { MutateUpdateLookupOptionMutation } from 'src/gql/graphql'
import { useInvalidateQueryByDocument } from 'src/hooks/useGraphQL'
import { Col, Row, Tooltip } from 'reactstrap'
import DateDisplay from 'src/components/DateDisplay'
import DeleteConfirmation from 'src/pages/lookups/Options/modals/Edit/DeleteConfirmation'
import useToggleState from 'src/hooks/useToggleState'

const fieldComponents: Record<string, React.FC<any>> = {
  Input,
  Dropdown,
  Checkboxes
}

interface FormProps {
  id?: string
  lookupId: string
  toggle: (id?: string) => void
}

const Form: React.FC<FormProps> = (props) => {
  const { toggle, id, lookupId } = props
  if (typeof id !== 'string') {
    return null
  }
  const [deleteTooltipIsOpen, , toggleDeleteTooltipIsOpen] = useToggleState(false)
  const [deleteConfirmationIsOpen, , toggleDeleteConfirmationIsOpen] = useToggleState(false)
  const options = useLookupOptions(lookupId ?? '')
  const config = useLookupConfig(lookupId ?? '')
  const option = options.find((option) => option._id === id)
  if (option === undefined) {
    throw new Error(`Option with id ${id} not found in lookup ${lookupId}`)
  }
  const hasRelationships = option.relationships?.some(relationship => Number(relationship?.count) > 0) ?? false
  const update = useMutateUpdateLookupOption()
  const invalidateOptions = useInvalidateQueryByDocument(lookupOptionsDocument)
  const handleSubmit = (data: MutateUpdateLookupOptionMutation): void => {
    const id = data.lookups?.update?._id
    if (typeof id === 'string') {
      void invalidateOptions({ key: lookupId })
      toggle(undefined)
    }
  }
  return (
    <Formik
      initialValues={{
        ...option
      }}
      onSubmit={async (values) => {
        update.mutate({
          id,
          key: lookupId,
          record: values
        }, {
          onSuccess: handleSubmit
        })
      }}
    >
      {({ handleSubmit, isValid }) => (
        <>
          <Body error={{ show: false, error: 'There was an issue saving the option.', details: '' }}>
            <div className='d-flex h-100 flex-column'>
              <div className='mb-auto'>
                {config?.fields?.map((field) => {
                  console.log('Field:', field)
                  if (field === null || field === undefined) {
                    console.log('Field is null or undefined:', field)
                    return null
                  }
                  const component = field?.component ?? {}
                  if (component.type === null || component.type === undefined) {
                    console.log('Field component type is null or undefined:', field)
                    return null
                  }
                  const FieldComponent = fieldComponents[component.type] ?? undefined
                  if (FieldComponent === undefined) {
                    console.log('Could not find a matching field component:', field)
                    return null
                  }
                  return (
                    <FieldComponent
                      key={field.key}
                      name={field.key}
                      label={field.title}
                      validate={(value: any) => {
                        if (component.props.required === true) {
                          if (value === undefined || value === null || value === '') {
                            return `${field.title ?? 'This'} is required.`
                          }
                        }
                      }}
                      {...component.props}
                    />
                  )
                })}
              </div>
              <div>
                <div className='border-bottom mb-2 fw-bold' />
                <Row className='small'>
                  <Col>
                    <Row>
                      <Col>
                        <b>Created:</b>{' '}
                        {option.createdAt === undefined || option.createdAt === null
                          ? 'Unknown'
                          : (
                            <DateDisplay display='distance' hover='system' date={option.createdAt} />
                            )}
                      </Col>
                    </Row>
                    <Row>
                      <Col><b>Created By:</b> {option._createdBy?.name ?? 'Unknown'}</Col>
                    </Row>
                  </Col>
                  <Col>
                    <Row>
                      <Col>
                        <b>Updated:</b>{' '}
                        {option.updatedAt === undefined || option.updatedAt === null
                          ? 'Unknown'
                          : (
                            <DateDisplay display='distance' hover='system' date={option.updatedAt} />
                            )}
                      </Col>
                    </Row>
                    <Row>
                      <Col><b>Updated By:</b> {option._updatedBy?.name ?? 'Unknown'}</Col>
                    </Row>
                  </Col>
                </Row>
              </div>

            </div>
          </Body>
          <Footer
            toggle={() => toggle(undefined)}
            onAction={handleSubmit}
            action={[
              {
                text: 'Save',
                icon: update.isLoading ? faSpinner : faSave,
                color: 'primary',
                disabled: !isValid || update.isLoading
              },
              {
                text: 'Delete',
                color: 'danger',
                disabled: hasRelationships || update.isLoading || deleteConfirmationIsOpen,
                wrapperProps: {
                  id: 'delete-button-wrapper'
                },
                onAction: () => {
                  toggleDeleteConfirmationIsOpen()
                },
                icon: faTrash,
                position: 'left'
              }
            ]}
          />
          <DeleteConfirmation
            id={option._id}
            lookupId={lookupId}
            toggle={toggleDeleteConfirmationIsOpen}
            isOpen={deleteConfirmationIsOpen}
            text={option.name ?? 'this option'}
            onDelete={() => {
              toggle(undefined)
            }}
          />
          <Tooltip
            isOpen={deleteTooltipIsOpen}
            target='delete-button-wrapper'
            toggle={() => {
              if (hasRelationships) {
                toggleDeleteTooltipIsOpen()
              }
            }}
            placement='top'
          >
            {hasRelationships
              ? 'This option cannot be deleted because it is used by other records.'
              : 'Delete this option.'}
          </Tooltip>
        </>
      )}
    </Formik>
  )
}

export default Form
